import { useCallback, useMemo, useRef, useState } from 'react'
import { format } from 'date-fns'
import { useTravelDates } from '../../Hooks/dates/useTravelDates'
import { useRoutes } from '../../Hooks/routes/useRoutes'
import { useTravelSearchContext } from '../../context/TravelSearchProvider'
import { ActiveSelectField, useActiveSelectField } from '../../context/ActiveSelectFieldProvider'
import { getDatepickerLocale } from '../../Utils'
import DateField from './DateField'
import { DatePicker, DateRangePicker } from '@dfds-pax/ui'
import { DatesData } from '@dfds-pax/ui/src/DatePicker/utils'

export default function DateSelector() {
  const [returnFocused, setReturnFocused] = useState(false)
  const selectorContainer = useRef<HTMLDivElement>(null as unknown as HTMLDivElement)

  const { routesApiData } = useRoutes()
  const { selectedOutboundDate, selectedHomeboundDate, isTravelDatesLoading, outboundTravelDates, homeboundTravelDates } = useTravelDates()
  const {
    dispatch,
    state: { isOneWay },
  } = useTravelSearchContext()
  const { activeField, toggleField, closeIfActive } = useActiveSelectField()

  // Determine if fields should be disabled
  const isDisabled = useMemo(() => {
    if (!outboundTravelDates) return false
    if (isOneWay) {
      return outboundTravelDates.dates.fromDate === outboundTravelDates.dates.toDate
    }
    if (!homeboundTravelDates) return false
    return (
      outboundTravelDates.dates.fromDate === outboundTravelDates.dates.toDate &&
      homeboundTravelDates.dates.fromDate === homeboundTravelDates.dates.toDate
    )
  }, [isOneWay, outboundTravelDates, homeboundTravelDates])

  const dataAttrList = useMemo(() => {
    return [
      {
        name: 'selected-outbound-date',
        value: selectedOutboundDate ? format(selectedOutboundDate, 'yyyy-MM-dd') : '',
      },
      {
        name: 'selected-homebound-date',
        value: selectedHomeboundDate ? format(selectedHomeboundDate, 'yyyy-MM-dd') : '',
      },
    ]
  }, [selectedOutboundDate, selectedHomeboundDate])

  // Handle the click that opens or closes the date picker
  const handleClick = useCallback(
    (clickedOutside: boolean) => {
      if (isDisabled) return
      setReturnFocused(false)
      if (!isTravelDatesLoading) {
        toggleField(ActiveSelectField.DATE, clickedOutside)
      }
    },
    [isDisabled, isTravelDatesLoading, toggleField]
  )

  const handleOutboundChange = useCallback(
    (date: Date) => {
      if (!isOneWay && routesApiData?.offerOrProductCode) {
        const tempHomeboundDateData: DatesData = {
          defaultDate: date.toString(),
          dates: {
            fromDate: date.toString(),
            toDate: date.toString(),
          },
          disabledDates: [],
          offerDates: [],
        }
        dispatch({
          type: 'SET_HOMEBOUND_TRAVEL_DATES',
          payload: tempHomeboundDateData,
        })
        dispatch({
          type: 'SET_DATES',
          dates: { outboundDate: date, homeboundDate: date },
        })
      } else {
        dispatch({
          type: 'SET_DATES',
          dates: {
            outboundDate: date,
            homeboundDate: isOneWay ? undefined : date,
          },
        })
      }

      if (isOneWay) {
        closeIfActive(ActiveSelectField.DATE)
      } else {
        setReturnFocused(true)
      }
    },
    [isOneWay, routesApiData, dispatch, closeIfActive]
  )

  const handleReturnChange = useCallback(
    (date: Date) => {
      dispatch({
        type: 'SET_DATES',
        dates: { outboundDate: selectedOutboundDate, homeboundDate: date },
      })
      setReturnFocused(false)
      closeIfActive(ActiveSelectField.DATE)
    },
    [dispatch, selectedOutboundDate, closeIfActive]
  )

  return (
    <div
      className="ts-h-[58px] ts-w-full ts-col-span-12 md:ts-col-span-6 lg:ts-col-span-2 ts-box-border ts-border lg:ts-border-r-0 ts-border-solid ts-border-cvi-grey-grey200"
      ref={selectorContainer}
    >
      <DateField
        dataAttrList={dataAttrList}
        isLoading={isTravelDatesLoading}
        disabled={isDisabled}
        showFooter={false}
        returnFocused={returnFocused}
        isOpen={activeField === ActiveSelectField.DATE}
        fieldName="date"
        onClick={handleClick}
        onClose={() => {
          setReturnFocused(false)
          closeIfActive(ActiveSelectField.DATE)
        }}
      >
        {selectedOutboundDate && isOneWay && (
          <DatePicker
            newStyles
            locale={getDatepickerLocale()}
            travelDates={outboundTravelDates}
            selectedDate={selectedOutboundDate}
            onDateChanged={(date) => date && handleOutboundChange(date)}
          />
        )}
        {selectedOutboundDate && selectedHomeboundDate && !isOneWay && (
          <DateRangePicker
            newStyles
            locale={getDatepickerLocale()}
            returnFocused={returnFocused}
            isLoading={isTravelDatesLoading}
            outboundTravelDates={outboundTravelDates}
            homeboundTravelDates={homeboundTravelDates}
            selectedDates={{ from: selectedOutboundDate, to: selectedHomeboundDate }}
            outboundDateChanged={handleOutboundChange}
            returnDateChanged={handleReturnChange}
            onClose={() => {
              setReturnFocused(false)
              closeIfActive(ActiveSelectField.DATE)
            }}
          />
        )}
      </DateField>
    </div>
  )
}
